import { notificationDanger } from '@/utils/notification'
import { apiGetAuth, apiGetAuthWithCustomHeadersResponseType } from '@/utils/api'
import { API_THEMATIC_MAPPING } from '@/utils/api-url'
import { exportCustomHeaderAndResponseType } from '@/utils/helper'

const state = () => {
  return {
    list_data: { meta: { total: 0 }, data: [] },
    summary_data: { meta: { total: 0 }, data: [] },
    circle_customer_data: { meta: { total: 0 }, data: [] },
    circle_store_data: { meta: { total: 0 }, data: [] },
    meta_filter: {},
    data_modal_add_edit: {},
    toggle_modal_add_edit_discard: {
      targetModals: false,
      title: '',
      description: '',
      customTitleBtnOk: '',
      customClass: '',
      type: '',
      loadingTable: false,
      loadingPopUp: false
    }
  }
}

const mutations = {
  SET_DATA(state, payload) {
    if (payload.meta && payload.data) {
      state.list_data = payload
    } else {
      state.list_data = {
        meta: { total: payload.length || 0 },
        data: payload || []
      }
    }
  },
  SET_SUMMARY_DATA(state, payload) {
    if (payload.meta && payload.data) {
      state.summary_data = payload
    } else {
      state.summary_data = {
        meta: { total: payload.length || 0 },
        data: payload || []
      }
    }
  },
  SET_CIRCLE_CUSTOMER_DATA(state, payload) {
    if (payload.meta && payload.data) {
      state.circle_customer_data = payload
    } else {
      state.circle_customer_data = {
        meta: { total: payload.length || 0 },
        data: payload || []
      }
    }
  },
  SET_CIRCLE_STORE_DATA(state, payload) {
    if (payload.meta && payload.data) {
      state.circle_store_data = payload
    } else {
      state.circle_store_data = {
        meta: { total: payload.length || 0 },
        data: payload || []
      }
    }
  },
  SET_DATA_MODAL_ADD_EDIT(state, payload) {
    state.data_modal_add_edit = payload
  },
  SET_TOGGLE_MODAL_ADD_EDIT_DISCARD(state, payload) {
    state.toggle_modal_add_edit_discard = payload
  },
  SET_META_FILTER(state, payload) {
    state.meta_filter = payload
  }
}

const actions = {
  async getListData(context, payload) {
    const newPayload = {
      ...payload
    }
    try {
      const response = await apiGetAuth(API_THEMATIC_MAPPING.LIST_CUSTOMER, newPayload)

      // Extract data based on your API format: { data: [...] }
      let responseData = response.data.data || response.data || []

      // Don't commit individual quadrant data to avoid overwriting
      // Let the component handle combining the data
      if (!payload.quadrant) {
        // Only commit if it's not a quadrant call
        const dataToCommit = {
          meta: { total: Array.isArray(responseData) ? responseData.length : 0 },
          data: Array.isArray(responseData) ? responseData : []
        }

        context.commit('SET_DATA', dataToCommit)
        context.commit('SET_META_FILTER', payload)
      }

      // Return the response data for quadrant processing
      return responseData
    } catch (error) {
      notificationDanger(error)
      throw error // Re-throw to handle in component
    }
  },
  // New method specifically for quadrant-based data fetching
  async getListDataQuadrants(context, quadrants) {
    try {
      // Create API calls for all quadrants
      const apiCalls = quadrants.map(quadrant => {
        const params = {
          ...quadrant.bounds,
          quadrant: quadrant.name
        }

        return apiGetAuth(API_THEMATIC_MAPPING.LIST_CUSTOMER, params)
          .then(response => ({
            quadrant: quadrant.name,
            data: response.data.data, // Based on your API format: { data: [...] }
            success: true
          }))
          .catch(error => ({
            quadrant: quadrant.name,
            error: error,
            success: false
          }))
      })

      // Execute all API calls simultaneously
      const results = await Promise.allSettled(apiCalls)

      // Process and combine results
      let allData = []

      results.forEach((result) => {
        if (result.status === 'fulfilled' && result.value.success) {
          const quadrantResult = result.value
          const quadrantData = quadrantResult.data

          // Handle the API response format: { data: [...] }
          if (Array.isArray(quadrantData)) {
            allData = [...allData, ...quadrantData]
          }
        }
      })

      // Remove duplicates based on the actual API response structure
      const uniqueData = []
      const seenIds = new Set()
      const seenConsumerIds = new Set()
      const seenCoordinates = new Set()

      allData.forEach(item => {
        // Create unique identifiers based on your API structure
        const itemId = item.id
        const consumerId = item.consumer_id
        const coordinateKey = `${item.latitude}_${item.longitude}`

        // Check for duplicates using multiple criteria
        const isUnique = !seenIds.has(itemId) &&
                        !seenConsumerIds.has(consumerId) &&
                        !seenCoordinates.has(coordinateKey)

        if (isUnique) {
          uniqueData.push(item)
          seenIds.add(itemId)
          if (consumerId) seenConsumerIds.add(consumerId)
          seenCoordinates.add(coordinateKey)
        }
      })

      // Create combined result
      const combinedData = {
        meta: { total: uniqueData.length },
        data: uniqueData
      }

      // Commit combined data to store
      context.commit('SET_DATA', combinedData)

      console.log(`Combined quadrant data: ${uniqueData.length} unique items from ${allData.length} total items`)

      return combinedData
    } catch (error) {
      notificationDanger(error)
      throw error
    }
  },

  // Enhanced version of existing methods to work with quadrants
  async getListDataWithQuadrants(context, payload) {
    try {
      // Check if bounds are provided
      if (!payload.top_left_lat || !payload.top_left_lon ||
          !payload.bottom_right_lat || !payload.bottom_right_lon) {
        // Fall back to regular API call if no bounds
        return context.dispatch('getListData', payload)
      }

      // Calculate center point
      const centerLat = (payload.top_left_lat + payload.bottom_right_lat) / 2
      const centerLon = (payload.top_left_lon + payload.bottom_right_lon) / 2

      // Create 4 quadrant definitions
      const quadrants = [
        {
          name: 'top_left',
          bounds: {
            ...payload,
            top_left_lat: payload.top_left_lat,
            top_left_lon: payload.top_left_lon,
            bottom_right_lat: centerLat,
            bottom_right_lon: centerLon
          }
        },
        {
          name: 'top_right',
          bounds: {
            ...payload,
            top_left_lat: payload.top_left_lat,
            top_left_lon: centerLon,
            bottom_right_lat: centerLat,
            bottom_right_lon: payload.bottom_right_lon
          }
        },
        {
          name: 'bottom_left',
          bounds: {
            ...payload,
            top_left_lat: centerLat,
            top_left_lon: payload.top_left_lon,
            bottom_right_lat: payload.bottom_right_lat,
            bottom_right_lon: centerLon
          }
        },
        {
          name: 'bottom_right',
          bounds: {
            ...payload,
            top_left_lat: centerLat,
            top_left_lon: centerLon,
            bottom_right_lat: payload.bottom_right_lat,
            bottom_right_lon: payload.bottom_right_lon
          }
        }
      ]

      // Use the quadrant-based method
      const result = await context.dispatch('getListDataQuadrants', quadrants)

      // Update meta filter with original payload
      context.commit('SET_META_FILTER', payload)

      return result
    } catch (error) {
      notificationDanger(error)
      throw error
    }
  },
  async getSummaryData(context, payload) {
    const newPayload = {
      ...payload
    }
    try {
      const response = await apiGetAuth(API_THEMATIC_MAPPING.GET_SUMMARY, newPayload)

      if (response.data.data && response.data.data.data && response.data.data.meta) {
        context.commit('SET_SUMMARY_DATA', {
          meta: response.data.data.meta,
          data: response.data.data.data || []
        })
      } else {
        context.commit('SET_SUMMARY_DATA', response.data.data)
      }
      context.commit('SET_META_FILTER', payload)
    } catch (error) {
      notificationDanger(error)
    }
  },
  async getDetailDataCustomerOnCircle(context, payload) {
    const newPayload = {
      ...payload
    }
    try {
      const response = await apiGetAuth(API_THEMATIC_MAPPING.LIST_CUSTOMER_ON_CIRCLE, newPayload)

      let responseData = { meta: { total: 0 }, data: [] }

      if (response && response.data) {
        if (response.data.data && Array.isArray(response.data.data)) {
          responseData = {
            meta: { total: response.data.data.length },
            data: response.data.data
          }
        } else if (response.data.data && response.data.data.data) {
          responseData = {
            meta: response.data.data.meta || { total: response.data.data.data.length },
            data: response.data.data.data
          }
        } else if (Array.isArray(response.data)) {
          responseData = {
            meta: { total: response.data.length },
            data: response.data
          }
        } else {
          // Fallback
          responseData = {
            meta: { total: 0 },
            data: response.data || []
          }
        }
      }
      context.commit('SET_CIRCLE_CUSTOMER_DATA', responseData)
      context.commit('SET_META_FILTER', payload)

      return responseData
    } catch (error) {
      notificationDanger(error)
      return { meta: { total: 0 }, data: [] }
    }
  },
  async getDetailDataStoreOnCircle(context, payload) {
    const newPayload = {
      ...payload
    }
    try {
      const response = await apiGetAuth(API_THEMATIC_MAPPING.LIST_STORE_ON_CIRCLE, newPayload)

      // Extract data correctly based on response structure
      let responseData = { meta: { total: 0 }, data: [] }

      if (response && response.data) {
        if (response.data.data && Array.isArray(response.data.data)) {
          // Direct array response
          responseData = {
            meta: { total: response.data.data.length },
            data: response.data.data
          }
        } else if (response.data.data && response.data.data.data) {
          // Nested data structure
          responseData = {
            meta: response.data.data.meta || { total: response.data.data.data.length },
            data: response.data.data.data
          }
        } else if (Array.isArray(response.data)) {
          // Direct array in response.data
          responseData = {
            meta: { total: response.data.length },
            data: response.data
          }
        } else {
          // Fallback
          responseData = {
            meta: { total: 0 },
            data: response.data || []
          }
        }
      }
      context.commit('SET_CIRCLE_STORE_DATA', responseData)
      context.commit('SET_META_FILTER', payload)

      return responseData
    } catch (error) {
      notificationDanger(error)
      return { meta: { total: 0 }, data: [] }
    }
  },
  async getDetailData(context, payload) {
    const newPayload = {
      ...payload
    }
    try {
      const response = await apiGetAuth(API_THEMATIC_MAPPING.LIST_DETAIL, newPayload)

      context.commit('SET_DATA', {
        meta: response.data.data.meta,
        data: response.data.data.data || []
      })
      context.commit('SET_META_FILTER', payload)
    } catch (error) {
      notificationDanger(error)
    }
  },
  async exportDetailData(_, payload) {
    try {
      const response = await apiGetAuthWithCustomHeadersResponseType(API_THEMATIC_MAPPING.EXPORT_DETAIL, payload)

      const currentDate = new Date().toISOString().split('T')[0]
      exportCustomHeaderAndResponseType(response.data, `Export Thematic Report Detail - ${currentDate}.xlsx`)
    } catch (error) {
      notificationDanger(error)
      throw error
    }
  },
  async exportSummaryData(_, payload) {
    try {
      const response = await apiGetAuthWithCustomHeadersResponseType(API_THEMATIC_MAPPING.EXPORT_SUMMARY, payload)

      const currentDate = new Date().toISOString().split('T')[0]
      exportCustomHeaderAndResponseType(response.data, `Export Thematic Report Summary - ${currentDate}.xlsx`)
    } catch (error) {
      notificationDanger(error)
      throw error
    }
  }
}

const getters = {
  list_data(state) {
    return state.list_data
  },
  summary_data(state) {
    return state.summary_data
  },
  circle_customer_data(state) {
    return state.circle_customer_data
  },
  circle_store_data(state) {
    return state.circle_store_data
  },
  get_toggle_modal_add_edit_discard(state) {
    return state.toggle_modal_add_edit_discard
  },
  get_data_modal_add_edit(state) {
    return state.data_modal_add_edit
  },
  meta_filter(state) {
    return state.meta_filter
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
